/**
 * VideoBackground — Reusable Video Background Component
 * 
 * Premium video background with dark overlay and glow effects.
 * Supports multiple formats (MOV, MP4, WebM) with automatic fallback.
 * 
 * @example
 * <VideoBackground src="/videos/hero-video1.mp4" poster="/videos/hero-poster.webp" overlay="dark" />
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface VideoBackgroundProps {
  /** Video source URL (can use .mov, .mp4, .webm) */
  src: string;

  /** Mobile video source URL (optional, for responsive video) */
  srcMobile?: string;

  /** Poster image shown before the video loads (improves LCP/percepção) */
  poster?: string;

  /** Poster image for mobile (< 768px). Falls back to `poster` when omitted. */
  posterMobile?: string;

  /** Overlay intensity: none | light | dark | premium */
  overlay?: 'none' | 'light' | 'dark' | 'premium';

  /** Enable top glow accent (cyan) */
  topGlow?: boolean;

  /** Enable bottom fade gradient */
  bottomFade?: boolean;

  /** Custom className for video element */
  videoClassName?: string;

  /** Custom className for container */
  containerClassName?: string;
}

// Helper function to detect if device is mobile (deterministic on first call)
const detectMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check window width
  const isMobileWidth = window.innerWidth < 768;
  
  // Additional check for touch devices
  const isTouchDevice = () => {
    return (
      (typeof window !== 'undefined' && 
        ('ontouchstart' in window ||
         navigator.maxTouchPoints > 0)) ||
      false
    );
  };
  
  // Use matchMedia as more reliable method
  const mediaQuery = window.matchMedia('(max-width: 767px)');
  
  return isMobileWidth || mediaQuery.matches;
};

export const VideoBackground = React.forwardRef<
  HTMLDivElement,
  VideoBackgroundProps
>(
  (
    {
      src,
      srcMobile,
      poster,
      posterMobile,
      overlay = 'dark',
      topGlow = true,
      bottomFade = true,
      videoClassName,
      containerClassName,
    },
    ref,
  ) => {
    // Detecta o tamanho de forma síncrona já no primeiro render para escolher
    // a fonte correta de imediato. Isso evita montar o <video> com o vídeo
    // desktop e, logo em seguida, remontar com o vídeo mobile (o que fazia o
    // navegador baixar OS DOIS vídeos — ~27 MB).
    const [isMobile, setIsMobile] = useState<boolean>(detectMobile);

    useEffect(() => {
      const checkMobile = () => setIsMobile(detectMobile());

      const mediaQuery = window.matchMedia('(max-width: 767px)');
      mediaQuery.addEventListener('change', checkMobile);
      window.addEventListener('resize', checkMobile);

      return () => {
        mediaQuery.removeEventListener('change', checkMobile);
        window.removeEventListener('resize', checkMobile);
      };
    }, []);

    // Usa o vídeo mobile quando disponível e em telas pequenas.
    const videoSrc = isMobile && srcMobile ? srcMobile : src;
    // Usa o poster mobile (mesmo enquadramento retrato do vídeo mobile) para
    // evitar o frame estático em paisagem sendo cortado por object-cover.
    const posterSrc = isMobile && posterMobile ? posterMobile : poster;
    // Generate alternative formats for fallback
    const getVideoSources = (url: string) => {
      const sources: { src: string; type: string }[] = [];
      const basePath = url.split('?')[0];

      if (basePath.endsWith('.mov')) {
        sources.push({ src: url, type: 'video/quicktime' });
        const mp4Url = url.replace('.mov', '.mp4');
        sources.push({ src: mp4Url, type: 'video/mp4' });
      } else if (basePath.endsWith('.mp4')) {
        sources.push({ src: url, type: 'video/mp4' });
      } else if (basePath.endsWith('.webm')) {
        sources.push({ src: url, type: 'video/webm' });
      }

      return sources;
    };

    const overlayClasses = {
      none: 'bg-transparent',
      light: 'bg-gradient-to-b from-black/30 via-black/20 to-black/50',
      dark: 'bg-gradient-to-b from-black/50 via-black/40 to-black/70',
      premium: 'bg-gradient-to-b from-black/60 via-black/50 to-black/80 backdrop-blur-sm',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'absolute inset-0 w-full h-full overflow-hidden z-0',
          containerClassName,
        )}
      >
        {/* Video Element */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={posterSrc}
          key={videoSrc}
          className={cn(
            'absolute inset-0 w-full h-full object-cover',
            videoClassName,
          )}
        >
          {getVideoSources(videoSrc).map((source, idx) => (
            <source key={idx} src={source.src} type={source.type} />
          ))}
          Your browser does not support the video tag.
        </video>

        {/* Overlay */}
        {overlay !== 'none' && (
          <div className={cn('absolute inset-0', overlayClasses[overlay])} />
        )}

        {/* Top Glow Accent (Optional) */}
        {topGlow && (
          <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-red-500/15 via-transparent to-transparent pointer-events-none" />
        )}

        {/* Bottom Fade (Optional) */}
        {bottomFade && (
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
        )}
      </div>
    );
  },
);

VideoBackground.displayName = 'VideoBackground';

export default VideoBackground;
